import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { InfoCard, CopyTextButton } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

function CodeBlock({ text }: { text: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        my: 1,
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography
        component="pre"
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          flex: 1,
          m: 0,
        }}
      >
        {text}
      </Typography>
      <CopyTextButton text={text} />
    </Box>
  );
}

export function MemorystoreCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  if (annotations['gcp.com/service'] !== 'memorystore') {
    return null;
  }

  const details = (entity.spec?.gcpDetails ?? undefined) as
    Record<string, unknown> | undefined;

  const {
    tier,
    redisVersion,
    memorySizeGb,
    host,
    port,
    transitEncryptionMode,
  } = (details ?? {}) as {
    tier?: string;
    redisVersion?: string;
    memorySizeGb?: number;
    host?: string;
    port?: number;
    transitEncryptionMode?: string;
  };

  const connectCmd =
    host !== undefined ? `redis-cli -h ${host} -p ${port ?? 6379}` : undefined;

  return (
    <InfoCard title="Memorystore (Redis)">
      <Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {tier && (
            <Chip
              label={tier}
              size="small"
              color={tier === 'STANDARD_HA' ? 'success' : 'default'}
              variant="outlined"
            />
          )}
          {redisVersion && (
            <Chip label={redisVersion} size="small" variant="outlined" />
          )}
          {memorySizeGb !== undefined && (
            <Chip
              label={`${memorySizeGb} GB`}
              size="small"
              variant="outlined"
            />
          )}
          {transitEncryptionMode && (
            <Chip
              label={`TLS: ${transitEncryptionMode}`}
              size="small"
              color={
                transitEncryptionMode === 'SERVER_AUTHENTICATION'
                  ? 'success'
                  : 'warning'
              }
              variant="outlined"
            />
          )}
        </Box>

        {connectCmd && (
          <>
            <Typography variant="subtitle2">Connect</Typography>
            <CodeBlock text={connectCmd} />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <Link
            href="https://cloud.google.com/memorystore/docs/redis/redis-overview"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            Memorystore Redis docs
          </Link>
        </Box>
      </Box>
    </InfoCard>
  );
}
